// ─── Types ────────────────────────────────────────────────────────────────────

export type WasteItem = {
  id:                       string
  name:                     string
  emoji:                    string
  freshness_category:       string
  avg_restock_cycle_days:   number | null
  avg_price:                number | null
  real_weekly_waste_dollars?: number | null
}

export type WasteEstimate = {
  itemId:                string
  itemName:              string
  itemEmoji:             string
  estimatedWasteDollars: number
  recommendation:        'buy_more_often' | 'freeze' | 'buy_less'
}

// ─── Category freshness windows (days) ───────────────────────────────────────
// Used only as a fallback when no real expiration data exists.
// These are conservative estimates — actual expiry dates always take priority.

const CATEGORY_FRESHNESS: Record<string, number> = {
  fresh_produce: 7,
  dairy:         10,
  meat_fish:     4,
  bread_bakery:  5,
}

// ─── estimateWeeklyWaste ──────────────────────────────────────────────────────

export function estimateWeeklyWaste(items: WasteItem[]): WasteEstimate[] {
  const results: WasteEstimate[] = []

  for (const item of items) {
    // Skip non-perishable categories
    if (item.freshness_category === 'pantry_staple') continue
    if (item.freshness_category === 'frozen')        continue

    // Skip items with missing restock data
    if (item.avg_restock_cycle_days === null) continue
    if (!item.avg_price)                      continue

    // Use real waste data from expiration_events when available
    if (item.real_weekly_waste_dollars != null) {
      if (item.real_weekly_waste_dollars <= 0) continue

      const freshnessWindow = CATEGORY_FRESHNESS[item.freshness_category] ?? 7
      const recommendation: WasteEstimate['recommendation'] =
        item.avg_restock_cycle_days > freshnessWindow * 1.5 ? 'buy_more_often'
        : freshnessWindow < 5 ? 'freeze'
        : 'buy_less'

      results.push({
        itemId:                item.id,
        itemName:              item.name,
        itemEmoji:             item.emoji,
        estimatedWasteDollars: item.real_weekly_waste_dollars,
        recommendation,
      })
      continue
    }

    // Fall back to category-based freshness window estimate
    const freshnessWindow = CATEGORY_FRESHNESS[item.freshness_category]
    if (!freshnessWindow) continue

    const wasteGap = item.avg_restock_cycle_days - freshnessWindow
    if (wasteGap <= 0) continue

    const wasteRatio            = wasteGap / freshnessWindow
    const estimatedWasteDollars = wasteRatio * item.avg_price

    const recommendation: WasteEstimate['recommendation'] =
      item.avg_restock_cycle_days > freshnessWindow * 1.5 ? 'buy_more_often'
      : freshnessWindow < 5 ? 'freeze'
      : 'buy_less'

    results.push({
      itemId:                item.id,
      itemName:              item.name,
      itemEmoji:             item.emoji,
      estimatedWasteDollars,
      recommendation,
    })
  }

  return results.sort((a, b) => b.estimatedWasteDollars - a.estimatedWasteDollars)
}
